import { Course } from '@components/courses/course-card'
import { getCourseNameFromAPI } from '@lib/constants/booking'
import {
  CourseGroup,
  isCampCourse,
  SUBJECT_ORDER,
} from '@lib/utils/course-categorization'

// Every filter is a set of selected values; an empty array means "no filter
// applied" (matches everything). Multiple selected values within a filter
// are OR'd together; different filters are AND'd together.
export type CourseFilterState = {
  age: string[]
  subject: string[]
  courseType: string[]
}

export type CourseFilterKey = keyof CourseFilterState

export const EMPTY_COURSE_FILTERS: CourseFilterState = {
  age: [],
  subject: [],
  courseType: [],
}

export type FilterOption = { value: string; label: string }

// --- Age: parse "Age 7-14" into a numeric range, so filtering can check
// whether a selected age falls inside a course's range, rather than
// requiring an exact string match.

type NumericRange = { min: number; max: number }

const parseNumericToken = (token: string): number | null => {
  const match = token.match(/\d+/)
  return match ? parseInt(match[0], 10) : null
}

const parseRange = (
  value: string | null | undefined,
  prefix: 'Age',
  parseToken: (token: string) => number | null
): NumericRange | null => {
  if (!value) return null
  const withoutPrefix = value.replace(new RegExp(`^${prefix}\\s*`, 'i'), '')
  const numbers = withoutPrefix
    .split('-')
    .map((part) => parseToken(part.trim()))
    .filter((n): n is number => n !== null)
  if (!numbers.length) return null
  return { min: Math.min(...numbers), max: Math.max(...numbers) }
}

export const parseAgeRange = (
  ageRange: string | null | undefined
): NumericRange | null => parseRange(ageRange, 'Age', parseNumericToken)

const buildValueOptionsFromRanges = (
  ranges: NumericRange[],
  toLabel: (value: number) => string
): FilterOption[] => {
  const values = new Set<number>()
  ranges.forEach((range) => {
    for (let value = range.min; value <= range.max; value++) values.add(value)
  })
  return Array.from(values)
    .sort((a, b) => a - b)
    .map((value) => ({ value: String(value), label: toLabel(value) }))
}

export const getAgeOptions = (courses: Course[]): FilterOption[] => {
  const ranges = courses
    .map((course) => parseAgeRange(course.ageRange))
    .filter((range): range is NumericRange => range !== null)
  return buildValueOptionsFromRanges(ranges, (age) => `Age ${age}`)
}

const rangeIncludesAnySelected = (
  range: NumericRange | null,
  selectedValues: string[]
): boolean =>
  !!range &&
  selectedValues.some((value) => {
    const selected = parseInt(value, 10)
    return selected >= range.min && selected <= range.max
  })

// --- Subject: the Coding/Math/ELA subject field used to group sections on
// the page. Coding is labeled "AI and Coding" since AI courses live under it.

export const SUBJECT_LABEL_OVERRIDES: Partial<Record<string, string>> = {
  Coding: 'AI & Coding',
  ELA: 'English Language Arts (ELA)',
}

export const getSubjectOptions = (courses: Course[]): FilterOption[] => {
  const present = new Set(
    courses.map((course) => course.subject).filter(Boolean)
  )
  return SUBJECT_ORDER.filter((subject) =>
    present.has(subject as Course['subject'])
  ).map((subject) => ({
    value: subject,
    label: SUBJECT_LABEL_OVERRIDES[subject] ?? getCourseNameFromAPI(subject),
  }))
}

// --- Course Type: Courses vs Camp Courses, using the same isCampCourse
// signal that separates the page's subject sections from its camp sections.

export const getCourseTypeOptions = (courses: Course[]): FilterOption[] => {
  const options: FilterOption[] = []
  if (courses.some((course) => !isCampCourse(course))) {
    options.push({
      value: 'course',
      label: 'Standard courses (long-duration)',
    })
  }
  if (courses.some((course) => isCampCourse(course))) {
    options.push({
      value: 'camp',
      label: 'Camp courses (short-duration)',
    })
  }
  return options
}

export const courseMatchesFilters = (
  course: Course,
  filters: CourseFilterState
): boolean => {
  if (
    filters.age.length > 0 &&
    !rangeIncludesAnySelected(parseAgeRange(course.ageRange), filters.age)
  ) {
    return false
  }
  if (
    filters.subject.length > 0 &&
    !filters.subject.includes(course.subject ?? '')
  ) {
    return false
  }
  if (filters.courseType.length > 0) {
    const courseType = isCampCourse(course) ? 'camp' : 'course'
    if (!filters.courseType.includes(courseType)) return false
  }
  return true
}

// Title matches are listed before description-only matches; 2 means no match.
const courseSearchRank = (course: Course, search: string): number => {
  if (course.title.toLowerCase().includes(search)) return 0
  if (course.description?.toLowerCase().includes(search)) return 1
  return 2
}

export const applyFiltersToGroups = (
  groups: CourseGroup[],
  filters: CourseFilterState,
  search = ''
): CourseGroup[] => {
  const query = search.trim().toLowerCase()
  return groups
    .map((group) => ({
      ...group,
      courses: group.courses
        .filter(
          (course) =>
            courseMatchesFilters(course, filters) &&
            courseSearchRank(course, query) < 2
        )
        .sort(
          (a, b) => courseSearchRank(a, query) - courseSearchRank(b, query)
        ),
    }))
    .filter((group) => group.courses.length > 0)
}

export const hasActiveCourseFilters = (filters: CourseFilterState): boolean =>
  Object.values(filters).some((values) => values.length > 0)

// --- URL query params: keep filter selections in the URL so a filtered
// courses page can be shared via its link. Each filter maps to one query key
// holding a comma-separated list of its selected values, e.g.
// `/courses/?age=8,9&subject=coding&course-type=camp`.

const COURSE_FILTER_QUERY_KEYS: Record<CourseFilterKey, string> = {
  age: 'age',
  subject: 'subject',
  courseType: 'course-type',
}

type RouterQuery = Record<string, string | string[] | undefined>

const parseQueryValues = (value: string | string[] | undefined): string[] => {
  if (value === undefined) return []
  const raw = Array.isArray(value) ? value.join(',') : value
  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
}

// Only keep values that are still valid options for the current course set, so
// a stale or hand-edited URL can't produce phantom chips or empty results.
// Matching is case-insensitive so a hand-edited or differently-cased URL
// (e.g. `subject=CODING`) still resolves — each match is mapped back to its
// canonical option value (e.g. `Coding`) to keep internal filter state
// consistent with `course.subject`.
const keepValidValues = (values: string[], options: FilterOption[]): string[] =>
  values
    .map(
      (value) =>
        options.find(
          (option) => option.value.toLowerCase() === value.toLowerCase()
        )?.value
    )
    .filter((value): value is string => value !== undefined)

export const parseCourseFiltersFromQuery = (
  query: RouterQuery,
  courses: Course[]
): CourseFilterState => ({
  age: keepValidValues(
    parseQueryValues(query[COURSE_FILTER_QUERY_KEYS.age]),
    getAgeOptions(courses)
  ),
  subject: keepValidValues(
    parseQueryValues(query[COURSE_FILTER_QUERY_KEYS.subject]),
    getSubjectOptions(courses)
  ),
  courseType: keepValidValues(
    parseQueryValues(query[COURSE_FILTER_QUERY_KEYS.courseType]),
    getCourseTypeOptions(courses)
  ),
})

// Returns the given query with every filter key removed, then re-added for any
// filter that currently has selections. Non-filter query params are preserved.
export const buildQueryWithCourseFilters = (
  query: RouterQuery,
  filters: CourseFilterState
): RouterQuery => {
  const next: RouterQuery = { ...query }
  ;(Object.keys(COURSE_FILTER_QUERY_KEYS) as CourseFilterKey[]).forEach(
    (key) => {
      const queryKey = COURSE_FILTER_QUERY_KEYS[key]
      const values = filters[key]
      if (values.length > 0) {
        next[queryKey] = values.map((value) => value.toLowerCase()).join(',')
      } else {
        delete next[queryKey]
      }
    }
  )
  return next
}
